import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { DeployStatusView } from "@/components/orchestration/DeployStatus";
import { ParameterForm } from "@/components/orchestration/ParameterForm";
import { PlanViewer } from "@/components/orchestration/PlanViewer";
import { RecommendationPanel } from "@/components/orchestration/RecommendationPanel";
import { ScanningBanner } from "@/components/orchestration/ScanningBanner";
import { Timeline } from "@/components/orchestration/Timeline";
import { Chevron } from "@/components/ui/Chevron";
import { openEventStream } from "@/lib/api/events";
import { getSession } from "@/lib/api/sessions";
import { useSessionStore } from "@/lib/state/session";

/**
 * Live orchestration view for a single session.
 *
 * Lifecycle:
 *   1. Hydrate the store with `getSession()` (handles refresh).
 *   2. Open the SSE stream.
 *   3. Pipe envelopes into the store until `session.terminal`.
 *   4. Switch sub-views by session state.
 */
export function Session() {
  const { id } = useParams<{ id: string }>();
  const reset = useSessionStore((s) => s.reset);
  const hydrate = useSessionStore((s) => s.hydrate);
  const consumeEvent = useSessionStore((s) => s.consumeEvent);

  const state = useSessionStore((s) => s.state);
  const events = useSessionStore((s) => s.events);
  const recommendation = useSessionStore((s) => s.recommendation);
  const parametersSchema = useSessionStore((s) => s.parametersSchema);
  const plan = useSessionStore((s) => s.plan);
  const finalStatus = useSessionStore((s) => s.finalStatus);
  const error = useSessionStore((s) => s.error);
  const scanFailures = useSessionStore((s) => s.scanFailures);

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    let closed = false;
    let streamClose: (() => void) | null = null;

    reset(id);

    void (async () => {
      try {
        const snap = await getSession(id, controller.signal);
        hydrate(snap);
        const stream = await openEventStream(id);
        streamClose = stream.close;
        for await (const envelope of stream.events) {
          if (closed) break;
          consumeEvent(envelope);
        }
      } catch (err) {
        if (!closed) {
          // eslint-disable-next-line no-console
          console.error("Session lifecycle error", err);
        }
      }
    })();

    return () => {
      closed = true;
      controller.abort();
      streamClose?.();
    };
  }, [id, reset, hydrate, consumeEvent]);

  if (!id) {
    return <p>Missing session id.</p>;
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr,18rem]">
      <div className="space-y-8">
        <header>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            <Chevron pulse={!isTerminal(state)} className="mr-2" />
            session {state}
          </p>
          <p className="font-mono text-xs text-muted-foreground">{id}</p>
        </header>

        {/* T0.3: separate "real" scan failures (sub-agent crashes / unreachable)
            from validation failures (handled inline by DeployStatusView). The
            ":validate" keys are user-input issues and don't belong here. */}
        {Object.entries(scanFailures).filter(
          ([k]) => !k.endsWith(":validate"),
        ).length > 0 ? (
          <aside className="rounded-md border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
            <p className="font-medium text-amber-500">
              Some sub-agents were skipped:
            </p>
            <ul className="mt-1 list-disc list-inside text-muted-foreground">
              {Object.entries(scanFailures)
                .filter(([k]) => !k.endsWith(":validate"))
                .map(([name, msg]) => (
                  <li key={name}>
                    <span className="font-mono">{name}</span>: {msg}
                  </li>
                ))}
            </ul>
          </aside>
        ) : null}

        {/* T0.4: scanning state used to be blank. Now we show what's being
            evaluated, so the user can see the LLM doing real work. */}
        {state === "scanning" ? <ScanningBanner /> : null}

        {recommendation && state === "awaiting_recommendation" ? (
          <RecommendationPanel sessionId={id} recommendation={recommendation} />
        ) : null}

        {parametersSchema && state === "awaiting_parameters" ? (
          <ParameterForm sessionId={id} schema={parametersSchema} />
        ) : null}

        {plan && state === "awaiting_deploy_confirmation" ? (
          <PlanViewer sessionId={id} plan={plan} />
        ) : null}

        <DeployStatusView
          state={state}
          finalStatus={finalStatus}
          error={error}
          scanFailures={scanFailures}
        />
      </div>

      <aside className="space-y-3">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          <Chevron className="mr-2" /> timeline
        </p>
        <Timeline events={events} />
      </aside>
    </div>
  );
}

function isTerminal(state: ReturnType<typeof useSessionStore.getState>["state"]): boolean {
  return state === "succeeded" || state === "failed" || state === "cancelled";
}
